import React, { useState, useEffect } from 'react';
import { invoicesAPI, contactsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Skeleton } from '../components/ui/skeleton';
import { Plus, Pencil, Trash2, Receipt, X, AlertCircle } from 'lucide-react';
import { formatCurrency, formatDate, statusLabels } from '../lib/utils';
import { toast } from 'sonner';

const STATUS_STYLES = {
  draft:     'status-badge status-draft',
  sent:      'status-badge status-sent',
  paid:      'status-badge status-paid',
  overdue:   'status-badge status-overdue',
  cancelled: 'status-badge status-cancelled',
};

export default function InvoicesPage() {
  const [invoices, setInvoices] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [contactId, setContactId] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [notes, setNotes] = useState('');
  const [taxRate, setTaxRate] = useState(20);
  const [items, setItems] = useState([{ description: '', quantity: 1, unit_price: 0 }]);
  const [saving, setSaving] = useState(false);

  const loadData = async () => {
    try {
      const [invRes, conRes] = await Promise.all([invoicesAPI.list(), contactsAPI.list()]);
      setInvoices(invRes.data);
      setContacts(conRes.data);
    } catch { toast.error('Erreur lors du chargement'); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadData(); }, []);

  const getContactName = (cId) => {
    const c = contacts.find(c => c.id === cId);
    return c ? `${c.first_name} ${c.last_name}` : '—';
  };

  const resetForm = () => {
    setContactId(''); setDueDate(''); setNotes('');
    setTaxRate(20); setItems([{ description: '', quantity: 1, unit_price: 0 }]);
  };

  const openCreateDialog = () => { setEditingInvoice(null); resetForm(); setDialogOpen(true); };
  const openEditDialog = (inv) => {
    setEditingInvoice(inv);
    setContactId(inv.contact_id);
    setDueDate(inv.due_date ? inv.due_date.split('T')[0] : '');
    setNotes(inv.notes || '');
    setTaxRate(inv.tax_rate);
    setItems(inv.items.length > 0 ? inv.items : [{ description: '', quantity: 1, unit_price: 0 }]);
    setDialogOpen(true);
  };

  const calculateTotals = () => {
    const subtotal = items.reduce((s, i) => s + Number(i.quantity) * Number(i.unit_price), 0);
    const taxAmount = subtotal * (taxRate / 100);
    return { subtotal, taxAmount, total: subtotal + taxAmount };
  };

  const addItem = () => setItems([...items, { description: '', quantity: 1, unit_price: 0 }]);
  const removeItem = (i) => { if (items.length > 1) setItems(items.filter((_, idx) => idx !== i)); };
  const updateItem = (i, field, value) => {
    const n = [...items]; n[i] = { ...n[i], [field]: value }; setItems(n);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const data = {
        contact_id: contactId,
        items: items.map(i => ({ description: i.description, quantity: parseInt(i.quantity) || 1, unit_price: parseFloat(i.unit_price) || 0 })),
        tax_rate: parseFloat(taxRate) || 20,
        due_date: dueDate || null,
        notes,
      };
      if (editingInvoice) {
        await invoicesAPI.update(editingInvoice.id, data);
        toast.success('Facture mise à jour');
      } else {
        await invoicesAPI.create(data);
        toast.success('Facture créée');
      }
      setDialogOpen(false);
      loadData();
    } catch { toast.error('Erreur lors de l\'enregistrement'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette facture ?')) return;
    try { await invoicesAPI.delete(id); toast.success('Facture supprimée'); loadData(); }
    catch { toast.error('Erreur lors de la suppression'); }
  };

  const handleStatusChange = async (invoice, newStatus) => {
    try {
      const updateData = { status: newStatus };
      if (newStatus === 'paid') updateData.paid_date = new Date().toISOString().split('T')[0];
      await invoicesAPI.update(invoice.id, updateData);
      toast.success('Statut mis à jour');
      loadData();
    } catch { toast.error('Erreur'); }
  };

  const isOverdue = (inv) => {
    if (inv.status === 'paid' || inv.status === 'cancelled') return false;
    if (!inv.due_date) return false;
    return new Date(inv.due_date) < new Date();
  };

  if (loading) {
    return (
      <div className="space-y-5" data-testid="invoices-loading">
        <div className="flex items-center justify-between"><Skeleton className="h-7 w-32" /><Skeleton className="h-10 w-40" /></div>
        <Skeleton className="h-96 rounded-xl" />
      </div>
    );
  }

  const { subtotal, taxAmount, total } = calculateTotals();
  const overdueCount = invoices.filter(isOverdue).length;
  const paidTotal = invoices.filter(i => i.status === 'paid').reduce((s, i) => s + Number(i.total || 0), 0);

  return (
    <div className="space-y-5 animate-fade-in" data-testid="invoices-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Factures</h1>
          <p className="text-sm text-muted-foreground mt-0.5">{invoices.length} factures au total</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={openCreateDialog} size="sm" className="gap-2" data-testid="add-invoice-btn">
              <Plus size={15} />Nouvelle facture
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingInvoice ? 'Modifier la facture' : 'Nouvelle facture'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Contact *</Label>
                  <Select value={contactId} onValueChange={setContactId}>
                    <SelectTrigger data-testid="invoice-contact"><SelectValue placeholder="Sélectionner un contact" /></SelectTrigger>
                    <SelectContent>
                      {contacts.map(c => (
                        <SelectItem key={c.id} value={c.id}>{c.first_name} {c.last_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Date d'échéance</Label>
                  <Input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} data-testid="invoice-due-date" />
                </div>
              </div>
              <div>
                <Label>Lignes de la facture</Label>
                <div className="space-y-2 mt-2">
                  {items.map((item, idx) => (
                    <div key={idx} className="flex flex-col sm:flex-row gap-2 p-2 bg-muted/20 rounded-lg sm:bg-transparent sm:p-0 sm:rounded-none sm:items-center">
                      <Input placeholder="Description" value={item.description} onChange={e => updateItem(idx, 'description', e.target.value)} className="sm:flex-1" data-testid={`invoice-item-desc-${idx}`} />
                      <div className="flex gap-2 items-center">
                        <Input type="number" min="1" placeholder="Qté" value={item.quantity} onChange={e => updateItem(idx, 'quantity', e.target.value)} className="w-20 flex-shrink-0" />
                        <Input type="number" min="0" step="0.01" placeholder="Prix" value={item.unit_price} onChange={e => updateItem(idx, 'unit_price', e.target.value)} className="w-28 flex-shrink-0" />
                        <div className="flex-1 sm:w-24 sm:flex-none text-right text-sm font-medium text-foreground">{formatCurrency(item.quantity * item.unit_price)}</div>
                        <button type="button" onClick={() => removeItem(idx)} disabled={items.length <= 1} className="p-1.5 text-muted-foreground hover:text-destructive disabled:opacity-30 transition-colors flex-shrink-0"><X size={15} /></button>
                      </div>
                    </div>
                  ))}
                </div>
                <Button type="button" variant="outline" size="sm" onClick={addItem} className="mt-2 gap-1.5"><Plus size={14} />Ajouter une ligne</Button>
              </div>
              <div className="border-t border-border pt-4 space-y-2 text-sm">
                <div className="flex justify-between text-muted-foreground"><span>Sous-total</span><span className="font-medium text-foreground">{formatCurrency(subtotal)}</span></div>
                <div className="flex justify-between items-center text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <span>TVA</span>
                    <Input type="number" min="0" max="100" value={taxRate} onChange={e => setTaxRate(e.target.value)} className="w-16 h-7 text-xs" />
                    <span>%</span>
                  </div>
                  <span className="font-medium text-foreground">{formatCurrency(taxAmount)}</span>
                </div>
                <div className="flex justify-between font-semibold text-base border-t border-border pt-2"><span>Total TTC</span><span className="text-primary">{formatCurrency(total)}</span></div>
              </div>
              <div><Label>Notes</Label><Textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} /></div>
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
                <Button type="submit" disabled={saving || !contactId} data-testid="save-invoice-btn">
                  {saving ? 'Enregistrement...' : (editingInvoice ? 'Mettre à jour' : 'Créer')}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Summary cards */}
      {invoices.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <div className="bg-card border border-border rounded-xl p-4 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">Total encaissé</p>
            <p className="text-xl font-bold text-emerald-600 dark:text-emerald-400">{formatCurrency(paidTotal)}</p>
          </div>
          <div className="bg-card border border-border rounded-xl p-4 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">En retard</p>
            <p className={`text-xl font-bold ${overdueCount > 0 ? 'text-destructive' : 'text-foreground'}`}>{overdueCount}</p>
          </div>
          <div className="bg-card border border-border rounded-xl p-4 shadow-sm col-span-2 md:col-span-1">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">Total factures</p>
            <p className="text-xl font-bold text-foreground">{invoices.length}</p>
          </div>
        </div>
      )}

      {/* ── Mobile card list ── */}
      <div className="md:hidden space-y-3">
        {invoices.length === 0 ? (
          <div className="text-center py-10 text-muted-foreground text-sm">Aucune facture trouvée</div>
        ) : (
          invoices.map(invoice => {
            const overdue = isOverdue(invoice);
            return (
              <div
                key={invoice.id}
                className={`border rounded-xl p-4 shadow-sm space-y-3 ${overdue ? 'bg-destructive/5 border-destructive/30' : 'bg-card border-border'}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <Receipt size={15} className="text-muted-foreground flex-shrink-0" />
                    <span className="font-semibold text-foreground">{invoice.invoice_number}</span>
                    {overdue && <AlertCircle size={14} className="text-destructive flex-shrink-0" />}
                  </div>
                  <Select value={invoice.status} onValueChange={v => handleStatusChange(invoice, v)}>
                    <SelectTrigger className="h-6 text-xs border-0 p-0 w-auto bg-transparent focus:ring-0">
                      <span className={STATUS_STYLES[invoice.status] || 'status-badge status-draft'}>
                        {statusLabels[invoice.status] || invoice.status}
                      </span>
                    </SelectTrigger>
                    <SelectContent>
                      {['draft', 'sent', 'paid', 'overdue', 'cancelled'].map(s => (
                        <SelectItem key={s} value={s}>{statusLabels[s]}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-foreground font-medium">{getContactName(invoice.contact_id)}</span>
                  <span className="text-lg font-bold text-foreground">{formatCurrency(Number(invoice.total || 0))}</span>
                </div>
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>Émise le {formatDate(invoice.created_at)}</span>
                  {invoice.due_date && (
                    <span className={overdue ? 'text-destructive font-medium' : ''}>
                      Échéance {formatDate(invoice.due_date)}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1 pt-1 border-t border-border">
                  <button onClick={() => openEditDialog(invoice)} className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">
                    <Pencil size={15} />
                  </button>
                  <button onClick={() => handleDelete(invoice.id)} className="p-1.5 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors">
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* ── Desktop table ── */}
      <div className="hidden md:block bg-card border border-border rounded-xl overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                {['N° Facture', 'Contact', 'Date', 'Échéance', 'Total', 'Statut', 'Actions'].map(h => (
                  <th key={h} className="text-left px-5 py-3.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground bg-muted/40 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {invoices.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-12 text-muted-foreground">Aucune facture trouvée</td></tr>
              ) : (
                invoices.map(invoice => {
                  const overdue = isOverdue(invoice);
                  return (
                    <tr
                      key={invoice.id}
                      className={`transition-colors ${overdue ? 'bg-destructive/5 hover:bg-destructive/10' : 'hover:bg-muted/30'}`}
                      data-testid={`invoice-row-${invoice.id}`}
                    >
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-2">
                          <Receipt size={15} className="text-muted-foreground" />
                          <span className="font-semibold text-foreground">{invoice.invoice_number}</span>
                          {overdue && <AlertCircle size={14} className="text-destructive" />}
                        </div>
                      </td>
                      <td className="px-5 py-4 text-foreground">{getContactName(invoice.contact_id)}</td>
                      <td className="px-5 py-4 text-muted-foreground">{formatDate(invoice.created_at)}</td>
                      <td className="px-5 py-4">
                        <span className={overdue ? 'text-destructive font-medium' : 'text-muted-foreground'}>
                          {invoice.due_date ? formatDate(invoice.due_date) : '—'}
                        </span>
                      </td>
                      <td className="px-5 py-4 font-semibold text-foreground">{formatCurrency(Number(invoice.total || 0))}</td>
                      <td className="px-5 py-4">
                        <Select value={invoice.status} onValueChange={v => handleStatusChange(invoice, v)}>
                          <SelectTrigger className="h-7 text-xs border-0 p-0 w-auto bg-transparent focus:ring-0">
                            <span className={STATUS_STYLES[invoice.status] || 'status-badge status-draft'}>
                              {statusLabels[invoice.status] || invoice.status}
                            </span>
                          </SelectTrigger>
                          <SelectContent>
                            {['draft', 'sent', 'paid', 'overdue', 'cancelled'].map(s => (
                              <SelectItem key={s} value={s}>{statusLabels[s]}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-1">
                          <button onClick={() => openEditDialog(invoice)} className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors" data-testid={`edit-invoice-${invoice.id}`}>
                            <Pencil size={15} />
                          </button>
                          <button onClick={() => handleDelete(invoice.id)} className="p-1.5 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors" data-testid={`delete-invoice-${invoice.id}`}>
                            <Trash2 size={15} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        {invoices.length > 0 && (
          <div className="px-5 py-3 border-t border-border bg-muted/20">
            <p className="text-xs text-muted-foreground">Affichage de {invoices.length} facture{invoices.length > 1 ? 's' : ''}</p>
          </div>
        )}
      </div>
    </div>
  );
}
